// Phase 43 — Manager-only mileage report. Detail page reached from the
// "Driver mileage (last 30 days)" tile on `/reports`. Shows a per-driver
// rollup table over a fixed 30-day window. (A date-range picker is on the
// backlog; the backend endpoint already supports `from`/`to` arbitrary
// ranges, but Phase 43 keeps the UI deliberately simple.)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronLeft, Route, RefreshCcw, AlertTriangle, Download } from "lucide-react";

function isoNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Phase 47 — persist last-selected mileage range across reloads.
const LS_KEY = "jb_mileage_range_v1";
function loadStoredRange() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.from === "string" && typeof parsed.to === "string"
        && /^\d{4}-\d{2}-\d{2}$/.test(parsed.from) && /^\d{4}-\d{2}-\d{2}$/.test(parsed.to)) {
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
}
function persistRange(r) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(r)); } catch { /* ignore */ }
}

export default function MileageReport() {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ rows: [], rollups: [] });

  // Phase 47 — date-range picker state. Honours a previously-saved
  // range, otherwise falls back to the last 30 days.
  const [range, setRange] = useState(() => loadStoredRange() || { from: isoNDaysAgo(29), to: todayIso() });
  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/reports/mileage", {
        params: { from: range.from, to: range.to },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load mileage report");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  // Phase 47 — apply the draft date inputs. Validates From ≤ To, persists
  // to localStorage, and triggers a reload via the `range` dep.
  const applyRange = () => {
    if (!draftFrom || !draftTo) {
      toast.error("Please pick both From and To dates");
      return;
    }
    if (draftFrom > draftTo) {
      toast.error("From date must be on/before To date");
      return;
    }
    const next = { from: draftFrom, to: draftTo };
    persistRange(next);
    setRange(next);
  };
  const resetRange = () => {
    const next = { from: isoNDaysAgo(29), to: todayIso() };
    setDraftFrom(next.from);
    setDraftTo(next.to);
    persistRange(next);
    setRange(next);
  };

  // Phase 44 — auth-aware blob download for CSV / XLSX export. Same
  // pattern used by `downloadCustomerExport` in the customer wizard.
  const exportFile = async (format) => {
    const token = localStorage.getItem("jb_token");
    try {
      const res = await fetch(
        `${API_BASE}/reports/mileage/export?from=${range.from}&to=${range.to}&format=${format}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `driver-mileage-${range.from}-${range.to}.${format === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Mileage export ready (${format})`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || e}`);
    }
  };

  const totals = data.rollups.reduce(
    (acc, r) => ({
      total_km: acc.total_km + Number(r.total_km || 0),
      total_minutes: acc.total_minutes + Number(r.total_minutes || 0),
      days_worked: acc.days_worked + Number(r.days_worked || 0),
    }),
    { total_km: 0, total_minutes: 0, days_worked: 0 }
  );

  return (
    <div className="page space-y-5" data-testid="mileage-report-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Logistics</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <Route className="h-8 w-8 text-accent" /> Driver mileage
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Last 30 days · home→first / last→home / inter-job legs. Voided
            bookings excluded. Drivers without a geocoded base address are
            still listed but contribute zero km.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" data-testid="mileage-report-back">
            <Link to="/reports"><ChevronLeft className="h-3 w-3 mr-1" /> Reports</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" data-testid="mileage-report-export-trigger">
                <Download className="h-3 w-3" /> Export <span className="text-[10px] opacity-60">▾</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="mileage-report-export-menu">
              <DropdownMenuItem onClick={() => exportFile("xlsx")} data-testid="mileage-export-xlsx">
                Export as Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportFile("csv")} data-testid="mileage-export-csv">
                Export as CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={load} disabled={busy} size="sm" data-testid="mileage-report-refresh">
            <RefreshCcw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Drivers" value={data.rollups.length} testid="mileage-kpi-drivers" />
        <Kpi label="Total km" value={totals.total_km.toFixed(1)} testid="mileage-kpi-total-km" />
        <Kpi label="Days worked" value={totals.days_worked} testid="mileage-kpi-days" />
        <Kpi label="Window" value={`${range.from} → ${range.to}`} testid="mileage-kpi-window" mono />
      </div>

      {/* Phase K — premium upsell + base-leg context.
          Free tenants who have set a company base see a friendly upsell card
          explaining that base→first / last→base legs are gated. Premium
          tenants see no card here; the base legs are surfaced inline on
          each driver row instead. */}
      {data.has_company_base === true && data.plan_allows_base_legs === false && (
        <div
          className="bg-amber-50 border border-amber-200 text-amber-900 rounded-[8px] p-3 flex items-start gap-3"
          data-testid="mileage-premium-upsell"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold uppercase tracking-wider mono text-[10px]">Premium upgrade</div>
            <div className="mt-0.5">
              Adds base→first and last→base legs to your mileage total. Currently showing job-to-job
              mileage only.
            </div>
          </div>
        </div>
      )}

      {/* Phase 47 — date-range picker. From / To inputs + Apply + Reset.
          The range is persisted to localStorage so the next visit
          remembers what the manager last chose. */}
      <div
        className="bg-card border border-border rounded-[8px] p-4 flex flex-wrap items-end gap-3"
        data-testid="mileage-range-picker"
      >
        <div className="space-y-1">
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</label>
          <Input
            type="date"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
            max={draftTo || undefined}
            className="w-[180px]"
            data-testid="mileage-range-from"
          />
        </div>
        <div className="space-y-1">
          <label className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</label>
          <Input
            type="date"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            min={draftFrom || undefined}
            className="w-[180px]"
            data-testid="mileage-range-to"
          />
        </div>
        <Button onClick={applyRange} disabled={busy} className="btn-cta" data-testid="mileage-range-apply">
          Apply range
        </Button>
        <Button variant="outline" onClick={resetRange} disabled={busy} data-testid="mileage-range-reset">
          Last 30 days
        </Button>
        <div className="text-[11px] text-muted-foreground ml-auto">
          Showing <b className="mono">{range.from}</b> → <b className="mono">{range.to}</b>
        </div>
      </div>

      <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm" data-testid="mileage-report-table">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-right px-3 py-2">Days worked</th>
              <th className="text-right px-3 py-2 hidden sm:table-cell">Base→first</th>
              <th className="text-right px-3 py-2 hidden sm:table-cell">Last→base</th>
              <th className="text-right px-3 py-2">Total km</th>
              <th className="text-right px-3 py-2">Avg km/day</th>
              <th className="text-right px-3 py-2">Drive time</th>
            </tr>
          </thead>
          <tbody>
            {data.rollups.map((r) => {
              const noBase = !r.has_base && !data.has_company_base;
              const mins = Math.round(Number(r.total_minutes || 0));
              const hh = Math.floor(mins / 60);
              const mm = mins - hh * 60;
              const showLegs = r.includes_base_legs;
              return (
                <tr key={r.staff_id} className="border-t border-border" data-testid={`mileage-row-${r.staff_id}`}>
                  <td className="px-3 py-2 font-bold">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{r.staff_name}</span>
                      {noBase && (
                        <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                              title="No base address geocoded — only inter-job legs are counted."
                              data-testid={`mileage-no-base-${r.staff_id}`}>
                          <AlertTriangle className="h-3 w-3" /> no base
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right mono">{r.days_worked}</td>
                  <td className="px-3 py-2 text-right mono text-muted-foreground hidden sm:table-cell"
                      data-testid={`mileage-base-to-first-${r.staff_id}`}>
                    {showLegs ? Number(r.base_to_first_km || 0).toFixed(1) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right mono text-muted-foreground hidden sm:table-cell"
                      data-testid={`mileage-last-to-base-${r.staff_id}`}>
                    {showLegs ? Number(r.last_to_base_km || 0).toFixed(1) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right mono font-bold">{Number(r.total_km).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right mono">{Number(r.avg_km_per_day || 0).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right mono">{hh > 0 ? `${hh}h ${String(mm).padStart(2, "0")}m` : `${mm}m`}</td>
                </tr>
              );
            })}
            {data.rollups.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No drivers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.rows.length > 0 && (
        <div className="bg-card border border-border rounded-[8px] overflow-x-auto">
          <div className="px-4 py-3 mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
            Daily breakdown
          </div>
          <table className="w-full text-sm" data-testid="mileage-report-daily-table">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Driver</th>
                <th className="text-right px-3 py-2">Legs</th>
                <th className="text-right px-3 py-2">Km</th>
                <th className="text-right px-3 py-2">Minutes</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.slice(0, 200).map((r, i) => (
                <tr key={`${r.staff_id}-${r.date}-${i}`} className="border-t border-border">
                  <td className="px-3 py-2 mono">{r.date}</td>
                  <td className="px-3 py-2">{r.staff_name}</td>
                  <td className="px-3 py-2 text-right mono">{r.legs_count}</td>
                  <td className="px-3 py-2 text-right mono font-bold">{Number(r.total_km).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right mono">{Math.round(Number(r.total_minutes || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length > 200 && (
            <div className="px-4 py-2 text-[11px] text-muted-foreground mono border-t border-border">
              Showing first 200 daily rows of {data.rows.length}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid, mono = false }) {
  return (
    <div className="bg-card border border-border rounded-[8px] p-4" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${mono ? "mono text-base" : ""}`}>{value}</div>
    </div>
  );
}
